// Слоты элементов библиотеки — правый канал ряда входов, зеркало кадров
// (`FrameRoleSlots`): та же плитка, та же рамка, та же прогрессивная активация.
// Элемент не файл, а доменная сущность (имя + набор картинок), поэтому в слоте
// показываем первую картинку как превью, а по клику открываем элемент целиком.

import { Images, Plus, TriangleAlert, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";
import { MediaThumb } from "~/shared/ui/media-thumb";

import type { Element } from "~/entities/element";

import { attachmentThumbFrame } from "./attachment-thumb-frame";
import { elementCoverUrl, elementSlotState } from "./element-slot-helpers";
import { emptySlotTile, SlotColumn } from "./SlotColumn";

export interface ElementSlotsProps {
    /** Выбранные элементы в порядке добавления. */
    elements: readonly Element[];
    /** Сколько слотов рисуем сразу (`mode.maxElements`). */
    maxElements: number;
    disabled: boolean;
    /** Элементы, чьё имя совпало со словом промпта — подсвечиваем предупреждением. */
    conflictingIds?: readonly string[];
    /**
     * Готовая подсказка «почему канал недоступен»; пусто — канал доступен.
     * Гасит только пустые слоты: уже выбранный элемент остаётся снимаемым,
     * иначе набор было бы нечем расцепить.
     */
    unavailableHint?: string;
    onOpenPicker: () => void;
    onRemove: (elementId: string) => void;
    /** Просмотр элемента целиком (лайтбокс с его картинками). */
    onView: (elementId: string) => void;
}

const SLOT_SIZE = "h-16 w-16";

export function ElementSlots({
    elements,
    maxElements,
    disabled,
    conflictingIds = [],
    unavailableHint,
    onOpenPicker,
    onRemove,
    onView,
}: ElementSlotsProps) {
    const { t } = useTranslation("chat");

    if (maxElements <= 0) return null;

    return (
        <div className="flex flex-wrap gap-3">
            {Array.from({ length: maxElements }, (_, index) => {
                const state = elementSlotState(index, elements.length);
                const element = elements[index];

                if (state === "filled" && element) {
                    const conflicting = conflictingIds.includes(element.id);
                    const cover = elementCoverUrl(element);
                    return (
                        <SlotColumn
                            key={element.id}
                            label={element.name}
                            labelTitle={
                                conflicting
                                    ? t("prompt.elements.nameCollisionHint", { name: element.name })
                                    : element.name
                            }
                            labelClassName={conflicting ? "text-amber-300" : undefined}
                        >
                            <div
                                className={cn(
                                    attachmentThumbFrame(false),
                                    SLOT_SIZE,
                                    conflicting && "border-amber-400/60",
                                )}
                            >
                                <button
                                    type="button"
                                    onClick={() => onView(element.id)}
                                    aria-label={t("prompt.elements.view", { name: element.name })}
                                    className="absolute inset-0 flex items-center justify-center"
                                >
                                    {cover ? (
                                        <MediaThumb url={cover} kind="image" className="absolute inset-0" />
                                    ) : (
                                        <Images className="h-5 w-5 text-zinc-500" aria-hidden />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => onRemove(element.id)}
                                    aria-label={t("prompt.elements.remove", { name: element.name })}
                                    className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5 text-zinc-300 backdrop-blur transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                                {conflicting && (
                                    <TriangleAlert
                                        className="absolute bottom-0.5 left-0.5 h-3 w-3 text-amber-300"
                                        aria-hidden
                                    />
                                )}
                            </div>
                        </SlotColumn>
                    );
                }

                const locked = state === "locked";
                const unavailable = unavailableHint !== undefined;
                return (
                    <SlotColumn key={`empty-${index}`}>
                        <button
                            type="button"
                            disabled={disabled || locked || unavailable}
                            onClick={onOpenPicker}
                            aria-label={t("prompt.elements.addSlot")}
                            title={
                                unavailable
                                    ? unavailableHint
                                    : locked
                                      ? (t("prompt.elements.previousFirst") ?? undefined)
                                      : undefined
                            }
                            className={emptySlotTile}
                        >
                            <Plus className="h-5 w-5" />
                        </button>
                    </SlotColumn>
                );
            })}
        </div>
    );
}
